/**
 * Ruteo de impresión: dado un documento, elige la impresora por ROL (mapa DOC_ROLE) y
 * despacha según su BACKEND — qz (escritorio silencioso), network (ePOS silencioso) o
 * browser (window.print universal). NUNCA lanza: si el camino silencioso falla, cae a
 * browser-print PERO reporta el fallo para que el operador SIEMPRE se entere (nunca en
 * silencio — con 80 pedidos una comanda perdida es comida que nadie prepara).
 */
import * as qz from '../lib/qz';
import { eposPrint } from './epos';
import { Printer, DocKind, DOC_ROLE, PaperWidth, ROLE_LABEL } from './types';

export interface PrintOutcome {
  ok: boolean;                                   // ¿llegó a papel (o al menos se abrió el diálogo)?
  method: 'qz' | 'network' | 'browser' | 'none';
  printerName?: string;
  role?: string;
  usedFallback: boolean;                         // cayó a browser tras fallar el silencioso
  error?: string;                                // motivo del fallo (para el aviso)
}

/** Abre una ventana y dispara la impresión del HTML (el doc trae su propio window.print). */
export function openPrintWindow(html: string): boolean {
  const w = window.open('', '_blank', 'width=420,height=680');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  return true;
}

/** Elige la impresora para un documento: rol correcto, activa, prefiere la sucursal activa. */
export function pickPrinter(printers: Printer[], kind: DocKind, branchId?: string | null): Printer | null {
  const role = DOC_ROLE[kind];
  const active = (printers ?? []).filter((p) => p.isActive && p.role === role);
  const byBranch = active.filter((p) => p.branchId === branchId);
  const tenantWide = active.filter((p) => !p.branchId);
  return byBranch[0] ?? tenantWide[0] ?? active[0] ?? null;
}

/**
 * Imprime en UNA impresora concreta (para "Imprimir prueba": no rutea por rol).
 * Mismo contrato: nunca lanza; si el silencioso falla, cae a browser y reporta.
 */
export async function printToPrinter(
  p: Printer,
  escpos: (w: PaperWidth) => string,
  html: (w: PaperWidth) => string,
): Promise<PrintOutcome> {
  const width: PaperWidth = p.paperWidth === 58 ? 58 : 80;
  const toBrowser = (error?: string): PrintOutcome => {
    const opened = openPrintWindow(html(width));
    return { ok: opened, method: 'browser', printerName: p.name, role: p.role, usedFallback: !!error, error: opened ? error : 'No se pudo abrir la ventana de impresión.' };
  };
  if (p.backendKind === 'browser') return toBrowser();
  try {
    if (p.backendKind === 'qz') {
      await qz.printEscPos(p.qzPrinterName || p.name, escpos(width));
      return { ok: true, method: 'qz', printerName: p.qzPrinterName || p.name, role: p.role, usedFallback: false };
    }
    await eposPrint(p.netHost, p.netPort, escpos(width));
    return { ok: true, method: 'network', printerName: p.name, role: p.role, usedFallback: false };
  } catch (e: any) {
    return toBrowser(`No se pudo imprimir en "${p.name}": ${e?.message || 'la impresora no respondió'}`);
  }
}

export interface PrintRequest {
  printers: Printer[];
  branchId?: string | null;
  kind: DocKind;
  escpos: (w: PaperWidth) => string;             // payload ESC/POS por ancho (para qz/network)
  html: (w: PaperWidth) => string;               // HTML por ancho para browser/fallback
}

export async function printDocument(req: PrintRequest): Promise<PrintOutcome> {
  const p = pickPrinter(req.printers, req.kind, req.branchId);
  const width: PaperWidth = p?.paperWidth === 58 ? 58 : 80;

  const toBrowser = (error?: string, printerName?: string): PrintOutcome => {
    const opened = openPrintWindow(req.html(width));
    return {
      ok: opened,
      method: 'browser',
      printerName,
      role: DOC_ROLE[req.kind],
      usedFallback: !!error,
      error: opened
        ? error
        : 'No se pudo abrir la ventana de impresión (¿el navegador bloqueó las ventanas emergentes?).',
    };
  };

  // Sin impresora del rol, o backend 'browser' explícito → camino universal (con diálogo).
  if (!p || p.backendKind === 'browser') {
    const missing = p ? undefined : `No hay impresora con rol "${ROLE_LABEL[DOC_ROLE[req.kind]]}" configurada para esta estación.`;
    return toBrowser(missing, p?.name);
  }

  try {
    if (p.backendKind === 'qz') {
      await qz.printEscPos(p.qzPrinterName || p.name, req.escpos(width));
      return { ok: true, method: 'qz', printerName: p.qzPrinterName || p.name, role: p.role, usedFallback: false };
    }
    await eposPrint(p.netHost, p.netPort, req.escpos(width));
    return { ok: true, method: 'network', printerName: p.name, role: p.role, usedFallback: false };
  } catch (e: any) {
    // El silencioso falló → cae a browser, pero NUNCA en silencio: reporta el motivo.
    return toBrowser(`No se pudo imprimir en "${p.name}": ${e?.message || 'la impresora no respondió'}`, p.name);
  }
}
